import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import mapboxgl from "mapbox-gl";
import { Select, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";

import Database from "./repository/Database";
import { ICONS } from "./Icons";
import ServiceControl from "./components/ServiceControl";

const App = () => {
  const database = new Database();
  const mapContainer = useRef(null);
  const mapRef = useRef();
  const geolocateControlRef = useRef();
  const currentMarkers = useRef([]);

  const [stateSelected, setStateSelected] = useState(-1);
  const [clusterDraw, setClusterDraw] = useState(false);
  const [isLoadMap, setIsLoadMap] = useState(false);

  const [viewport, setViewport] = useState({
    latitude: 5.8422299,
    longitude: -85.0085648,
    zoom: 15,
  });

  const [error, setError] = useState(null);

  const handleError = (error) => {
    setError(error.message);
  };

  const distance = function (lat1, lon1, lat2, lon2) {
    const toRad = (value) => (value * Math.PI) / 180;

    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const calculateState = function (latitude, longitude) {
    const stateCheck = database
      .getAllStates()
      .find(
        (state) =>
          distance(
            state.geometry.coordinates[1],
            state.geometry.coordinates[0],
            latitude,
            longitude
          ) <= 30
      );

    if (stateCheck && stateCheck.id !== stateSelected) {
      setStateSelected(stateCheck.id);

      cleanMarkers();
      drawMarkers(stateCheck.id);
    }
  };

  const drawMarkers = function (stateId) {
    if (clusterDraw) {
      return;
    }

    const pointsSet = database.searchPoints(stateId);
    pointsSet.forEach((service, key) => {
      if (service.data.length <= 0) {
        return;
      }

      service.data.forEach((point) => {
        const el = document.createElement("div");
        const width = 24;
        const height = 24;
        el.className = "marker";
        el.style.backgroundImage = ICONS.get(key).marker;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        el.style.backgroundSize = "100%";
        el.style.display = "block";
        el.style.border = "none";
        el.style.borderRadius = "50%";
        el.style.cursor = "pointer";
        el.style.padding = 0;

        const marker = new mapboxgl.Marker(el)
          .setLngLat([
            point.geometry.coordinates[0],
            point.geometry.coordinates[1],
          ])
          .setPopup(
            new mapboxgl.Popup().setHTML(
              `<h3>${point.properties.name}</h3><span>${point.properties.address}</span>`
            )
          )
          .addTo(mapRef.current);

        currentMarkers.current.push(marker);
      });
    });
  };

  const cleanMarkers = function () {
    currentMarkers.current.forEach((marker) => {
      marker.remove();
    });

    currentMarkers.current = [];
  };

  const handleViewport = function (data) {
    mapRef.current.setCenter([data.longitude, data.latitude]);
    mapRef.current.setZoom(data.zoom);

    setViewport(data);
  };

  const handleState = function (e) {
    const stateId = e.target.value;

    if (stateId !== -1) {
      const state = database.getState(e.target.value);

      handleViewport({
        latitude: state.geometry.coordinates[1],
        longitude: state.geometry.coordinates[0],
        zoom: 15,
      });
    }

    setStateSelected(stateId);
  };

  const handleClusterDraw = function (isClusterDraw) {
    setClusterDraw(isClusterDraw);
    if (isClusterDraw) {
      cleanMarkers();
      mapRef.current.setLayoutProperty("clusters", "visibility", "visible");
      mapRef.current.setLayoutProperty("cluster-count", "visibility", "visible")
      
    } else {
      mapRef.current.setLayoutProperty("clusters", "visibility", "none");
      mapRef.current.setLayoutProperty("cluster-count", "visibility", "none")
      
      drawMarkers(stateSelected);
    }
  };

  const ServicePaper = styled(Paper)(({ theme }) => ({
    width: 140,
    height: 100,
    padding: theme.spacing(2),
    ...theme.typography.body2,
    textAlign: "center",
    backgroundColor: "#F5F5F5",
  }));

  const handleServiceControl = function (service, isDisable) {
    database.disableService(service, isDisable);

    cleanMarkers();
    drawMarkers(stateSelected);
  };

  useEffect(() => {
    mapboxgl.accessToken =
      "pk.eyJ1IjoibWlndWVsZHVxdWU0NTQ1IiwiYSI6ImNsenI1OGhnYjFidnkybXB3eHZkZHZvdGgifQ.-tDBBpm8G9RxN3GJwGxjlw";

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      zoom: 15,
      center: [-79.6304765, 5.9078057],
    });

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        handleViewport({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          zoom: 15,
        });

        cleanMarkers();
        drawMarkers(-1);
      }, handleError);
    }

    geolocateControlRef.current = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
      showUserLocation: true,
    });

    geolocateControlRef.current.on("geolocate", (position) => {
      console.log("A geolocate event has occurred.");
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-left");
    mapRef.current.addControl(geolocateControlRef.current, "bottom-left");

    mapRef.current.on("style.load", () => {
      mapRef.current.setFog({});
      geolocateControlRef.current.trigger();

      mapRef.current.addSource("clusterservices", {
        type: "geojson",
        data: database.getClusters(),
        cluster: true,
        clusterRadius: 20,
      });

      // Add the main circle layer (for the actual clusters)
      mapRef.current.addLayer({
        id: "clusters",
        type: "circle",
        source: "clusterservices",
        filter: ["has", "point_count"], // Only for clusters
        paint: {
          "circle-color": [
            "step",
            ["get", "point_count"],
            "rgba(65, 145, 225, 1)", // Color for smaller clusters
            20,
            "rgba(14, 94, 174, 1)", // Color for medium clusters
            100,
            "rgba(7, 47, 87, 1)", // Color for large clusters
          ],
          "circle-radius": [
            "step",
            ["get", "point_count"],
            15, // Radius for smaller clusters
            20,
            25, // Larger radius for medium clusters
            100,
            30, // Even larger for big clusters
          ],
          "circle-stroke-width": 3,
          "circle-stroke-color": "rgba(65, 145, 225, 0.3)",
        },
      });

      mapRef.current.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "clusterservices",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
        paint: {
          "text-color": "#FFFFFF",
        },
      });

      setIsLoadMap(true);
    });

    mapRef.current.on("mousedown", () => {});

    mapRef.current.on("moveend", () => {});

    mapRef.current.on("zoomend", () => {
      if (mapRef.current.getZoom() < 14.5) {
        handleClusterDraw(true);
      } else {
        handleClusterDraw(false);
      }
    });

    return () => {
      mapRef.current.remove();
    };
  }, []);

  return (
    <Container fixed>
      <Box component="section" sx={{ p: 2, border: "1px" }}>
        <Typography
          align="center"
          variant="h6"
          sx={{
            color: "#323232",
            fontStyle: "bold",
            fontWeight: "bold",
            fontFamily: "Poppins",
          }}
        >
          ¿Dónde puedes usar Flypass?
        </Typography>
        <Typography
          align="center"
          variant="h2"
          sx={{
            color: "#323232",
            fontStyle: "bold",
            fontWeight: "bold",
            fontFamily: "Poppins",
          }}
        >
          Cobertura Flypass
        </Typography>
      </Box>
      <Box component="section" sx={{ marginBottom: "20px" }}>
        <Grid container>
          <Grid
            xs={12}
            md={2}
            style={{ border: "16px #F5F5F5", background: "#F5F5F5", borderRadius: "20px 0px 0px 20px" }}
          >
            <Box sx={{ margin: 4 }}>
              <Typography
                
                align="center"
                variant="h6"
                sx={{
                  color: "#323232",
                  fontStyle: "bold",
                  fontFamily: "Poppins",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                Servicios
              </Typography>
            </Box>
            <Box sx={{ p: 0 }} md={{ p: 1 }}>
              <Stack
                direction="row"
                spacing={{ xs: 2, sm: 2 }}
                justifyContent="center"
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                {Array.from(database.getServices()).map(([service, data]) => {
                  return (
                    <ServiceControl
                      key={service}
                      label={data.name}
                      type={service}
                      onCallbackClick={handleServiceControl}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Grid>
          <Grid xs={12} md={10} style={{ position: "relative" }}>
            <Box
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                zIndex: "1000",
              }}
            >
              <Select
                size="small"
                onChange={handleState}
                value={stateSelected}
                sx={{
                  color: "black",
                  backgroundColor: "white",
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  ".MuiSvgIcon-root": {
                    color: "black",
                  },
                }}
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione una opci&oacute;n
                </MenuItem>
                {database.getAllStates().map((state) => {
                  return (
                    <MenuItem key={state.id} value={state.id}>
                      {state.properties.state}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
            <Box sx={{ height: "540px" }} md={{ height: "600px" }}>
              <div
                ref={mapContainer}
                style={{
                  top: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "0px 20px 20px 0px",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        component="section"
        sx={{
          p: 2,
          border: "1px #F5F5F5",
          background: "#F5F5F5",
          borderRadius: "20px",
        }}
        md={{
          p: 2,
          border: "1px #F5F5F5",
          background: "#F5F5F5",
          borderRadius: "20px",
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          justifyContent="center"
          useFlexGap
          sx={{ flexWrap: "wrap" }}
        >
          {Array.from(database.getServices()).map(([service, data]) => {
            return (
              <ServicePaper key={service} elevation={0}>
                <img
                  src={ICONS.get(service).button}
                  alt={data.name}
                  title={data.name}
                  width="48px"
                  height="48px"
                />
                <Typography
                  align="center"
                  variant="h6"
                  sx={{
                    color: "#323232",
                    fontStyle: "bold",
                    fontFamily: "Poppins",
                    fontWeight: "bold",
                    fontSize: "18px",
                  }}
                >
                  {data.length}
                </Typography>
                <Typography
                  align="center"
                  variant="h6"
                  sx={{
                    color: "#323232",
                    fontStyle: "bold",
                    fontFamily: "Poppins",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {data.name}
                </Typography>
              </ServicePaper>
            );
          })}
        </Stack>
      </Box>
    </Container>
  );
};

export default App;
